<template>
  <RouterLink :to="localizedTo" v-bind="$attrs">
    <slot></slot>
  </RouterLink>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { RouterLink, useRoute } from "vue-router";

const props = defineProps({
  // Can be a string path or a route object
  to: {
    type: [String, Object],
    required: true,
  },
  // Force a specific locale instead of using the current one
  locale: {
    type: String,
    default: "",
  },
});

const route = useRoute();

// Get the current locale from the URL path
const currentLocale = computed(() => {
  const pathSegments = route.path.split("/");
  return pathSegments.length > 1 ? pathSegments[1] : "en";
});

// Use provided locale or current locale
const activeLocale = computed(() => props.locale || currentLocale.value);

// Process the destination to include the locale prefix
const localizedTo = computed(() => {
  // If it's already a full route object
  if (typeof props.to === "object") {
    // If the path already includes a locale prefix, don't modify it
    if (props.to.path && props.to.path.match(/^\/(en|ru|uz)(\/|$)/)) {
      return props.to;
    }

    // Clone the route object to avoid mutating the original
    const localizedRoute = { ...props.to };

    // Add locale prefix to path if path exists
    if (localizedRoute.path) {
      // Check if path starts with a slash, if not add one
      const path = localizedRoute.path.startsWith("/")
        ? localizedRoute.path
        : `/${localizedRoute.path}`;

      localizedRoute.path = `/${activeLocale.value}${path}`;
    }
    // If using named routes, try to find the localized route name
    else if (localizedRoute.name) {
      const routeName = String(localizedRoute.name);
      // Check if the name already includes locale prefix
      if (!routeName.startsWith(`${activeLocale.value}-`)) {
        localizedRoute.name = `${activeLocale.value}-${routeName}`;
      }
    }

    return localizedRoute;
  }

  // If it's a string path
  const to = String(props.to);

  // If it's an absolute URL or anchor, return as is
  if (to.startsWith("http") || to.startsWith("#")) {
    return to;
  }

  // If the path already includes a locale prefix, don't modify it
  if (to.match(/^\/(en|ru|uz)(\/|$)/)) {
    return to;
  }

  // Check if path starts with a slash
  const path = to.startsWith("/") ? to : `/${to}`;

  // Add locale prefix
  return `/${activeLocale.value}${path}`;
});
</script>
